/**
 * Orchestrator: wires config + adapter + events; registers features.
 * @see docs/architecture/GREENFIELD-LECTURA-ARCHITECTURE.md
 */

import { validateConfig, getConfig } from './core/config';
import { on, emit, EVENTS } from './core/events';
import { createBar, ensureSlidesWrap, renderFab } from './shell';
import { createDrawerDOM } from './shell/shell-drawer';
import { createDrawerController } from './shell/drawer-controller';
import { registerRevealTweaks } from './reveal-tweaks';
import { registerAuth } from './features/auth';
import { registerChat, isChatToastDebugEnabled } from './features/chat';
import { registerNotes } from './features/notes';
import { registerRating } from './features/rating';
import { registerTTS } from './features/tts';
import { registerProfile } from './features/profile';
import { registerRemoteV1 } from './features/register-remote-v1';
import { resolveRemoteSessionApi } from './services/remote/v1/session-api';
import { appendRemotePreviewQueryParams } from './services/remote/v1/remote-preview-url';
import { mergeRemoteV1SessionAuth } from './services/remote/v1/merge-remote-v1-session-auth';
import { resolveDeckBasePath } from './services/remote-session';
import { registerResources } from './features/resources';
import { registerSpeakerNotes } from './features/speaker-notes';
import { getSettingsSectionFromLocation, isSettingsSectionId } from './features/settings-page';
import { createRuntimeDependencies } from './services/runtime-dependencies';
import { serializeQueueItemToCodeword, type QueueItem } from './services/tts-queue';
import { remoteTtsVerbose } from './lib/remote-tts-log';
import type { LecturaHostAdapter, LecturaConfig, LecturaApi } from './core/types';

export interface InitOpts {
  drawerOnly?: boolean;
  /** Slides-only embed inside remote preview iframe (skip Reveal tweaks that assume full UI). */
  embedPreview?: boolean;
  /** Remote shell: mount the Extra resources toolbar button here (drawer-only). */
  resourcesBarSlot?: HTMLElement;
  /** Remote: true when MQTT session role is presenter or display (resources button + manage UI). */
  sessionCanManageResources?: () => boolean;
  /** Remote shell, MQTT role presenter: default drawer open on Speaker notes tab + show that tab. */
  remotePresenterSession?: boolean;
  /**
   * Remote shell (`drawerOnly`): forward speaker-note codeword clicks to the deck via MQTT (`TTS_RUN`).
   * When unset, codewords in drawer-only mode are ignored (deck lives in the preview iframe).
   */
  sendRemoteTtsCodeword?: (codeword: string) => void;
}

export function init(
  userConfig: Partial<LecturaConfig>,
  adapter: LecturaHostAdapter,
  opts: InitOpts = {}
): LecturaApi {
  const config = getConfig(userConfig);
  validateConfig(config);

  /** Chat, notes, resources, and roles key by adapter.getPresId(); without this, every deck fell back to literal "deck". */
  if (typeof window !== 'undefined') {
    const w = window as unknown as { __LECTURA_PRES_ID__?: string };
    if (!String(w.__LECTURA_PRES_ID__ ?? '').trim()) {
      w.__LECTURA_PRES_ID__ = config.presentationId;
    }
  }

  const drawerOnly = opts.drawerOnly === true;
  const embedPreview = opts.embedPreview === true;
  const {
    authService,
    chatStorage,
    notesStorage,
    ratingStorage,
    resourcesStorage,
    aiResourcesService,
    chatMentionNotify,
    presentationManagementService,
    wrappedAdapter,
    chatRoleService
  } = createRuntimeDependencies(
    config,
    adapter,
    drawerOnly
  );

  let mountPoint = adapter.getDrawerMountPoint();
  if (!drawerOnly) {
    mountPoint = ensureSlidesWrap();
  }

  const chatMaxLength = config.chat && typeof config.chat.chatMaxLength === 'number' ? config.chat.chatMaxLength : 500;
  const dom = createDrawerDOM({
    showChatTab: config.features.chat,
    showNotesTab: config.features.notes,
    /** Remote viewer shell (`/remote/`) is drawer-only but still needs the Speaker notes tab + registration. */
    showSpeakerNotesTab: !drawerOnly || config.__lecturaRemoteViewerShell === true,
    chatMaxLength
  });
  const drawerStatePresId = wrappedAdapter.getPresId() || config.presentationId;
  const { drawerApi, drawerView } = createDrawerController({
    mountPoint,
    dom,
    presentationId: drawerStatePresId,
    remotePresenterInitialSpeaker: opts.remotePresenterSession === true,
    onOpenChange: (isOpen: boolean) => {
      adapter.onDrawerOpen?.(isOpen);
      emit(EVENTS.DRAWER_OPEN, isOpen);
    }
  });

  adapter.subscribeSlideChange(() => emit(EVENTS.SLIDE_CHANGED));
  adapter.subscribeUserChange(() => emit(EVENTS.USER_CHANGED));
  if (authService.subscribeUserChange) {
    authService.subscribeUserChange(() => emit(EVENTS.USER_CHANGED));
  }

  if (!drawerOnly && typeof Reveal !== 'undefined' && !embedPreview) {
    registerRevealTweaks();
  }

  let ttsApi: { toggleTTS: () => void; runCodeword: (item: unknown) => Promise<void> } | undefined;
  let fabBadgeEl: HTMLElement | null = null;
  let chatBarSlotEl: HTMLElement | null = null;
  const needBar = (config.showFab || config.features.tts || config.features.profile || config.features.remote || config.features.resources) && !drawerOnly;
  if (needBar) {
    const revealEl = document.querySelector('.reveal');
    const barContainer = (revealEl as HTMLElement) || mountPoint;
    const barResult = createBar(barContainer);
    chatBarSlotEl = barResult.slots.chat;
    // So plugin/notes-tts (when loaded) mounts its TTS UI into our bar slot
    if (typeof window !== 'undefined') window.__lecturaTtsContainer = barResult.slots.tts;
    if (config.showFab) {
      const fabResult = renderFab(() => drawerApi.toggle(), {
        fabLabel: config.fabLabel,
        container: barResult.slots.chat
      });
      if (fabResult) fabBadgeEl = fabResult.badge;
    }
    if (config.features.tts) {
      const RevealNotesTTS = typeof window !== 'undefined' ? (window as unknown as { RevealNotesTTS?: { init: (r: typeof Reveal) => void } }).RevealNotesTTS : undefined;
      if (RevealNotesTTS?.init && typeof Reveal !== 'undefined') {
        RevealNotesTTS.init(Reveal);
      } else {
        ttsApi = registerTTS(wrappedAdapter, { ttsSlot: barResult.slots.tts });
      }
    }
    if (config.features.profile) {
      const profileApi = registerProfile(authService, { profileSlot: barResult.slots.profile }, {
        getSlidesVersion: () => config.slidesVersion,
        roleService: chatRoleService,
      presentationService: presentationManagementService,
      getPresentationId: () => wrappedAdapter.getPresId() || config.presentationId
      });
      if (typeof window !== 'undefined' && profileApi) {
        (window as unknown as { __lecturaOpenProfileSettings?: (section?: string) => void }).__lecturaOpenProfileSettings = (section?: string) => {
          const raw = String(section || '').trim().toLowerCase();
          profileApi.openSettings(isSettingsSectionId(raw) ? raw : 'tts');
        };
        const sectionFromQuery = getSettingsSectionFromLocation(window.location.href);
        if (sectionFromQuery) {
          window.setTimeout(() => profileApi.openSettings(sectionFromQuery), 0);
        }
      }
    }
    if (config.features.remote) {
      const mqttUrl =
        String((config.chat as { mqtt?: { url?: string } } | null)?.mqtt?.url || '').trim() ||
        'wss://broker.hivemq.com:8884/mqtt';
      const configForRemote = mergeRemoteV1SessionAuth(config, authService);
      const sessionApi = resolveRemoteSessionApi(configForRemote, mqttUrl, () => {
        const base = resolveDeckBasePath();
        const origin = window.location.origin;
        const path = base === '/' ? '' : base;
        return appendRemotePreviewQueryParams(`${origin}${path}/?postMessageEvents=true`, origin);
      });
      const gateSessionOnFirebaseToken =
        config.remote?.v1?.devMock !== true && typeof authService.getIdToken === 'function';
      registerRemoteV1({
        remoteSlot: barResult.slots.remote,
        getPresentationId: () => wrappedAdapter.getPresId() || config.presentationId,
        mqttUrlFallback: mqttUrl,
        slidesVersion: config.slidesVersion,
        sessionApi,
        ...(gateSessionOnFirebaseToken ? { getRemoteSessionBearer: () => authService.getIdToken!() } : {}),
        ...(typeof authService.getCurrentUser === 'function'
          ? {
              getRemoteSessionUserKey: () => authService.getCurrentUser?.()?.uid ?? null
            }
          : {})
      });
    }
    if (barResult.barSlideNumTextEl) {
      const updateBarSlideNum = (): void => {
        const cur = wrappedAdapter.getSlideIndex();
        const total = Math.max(1, wrappedAdapter.getTotalSlides());
        barResult.barSlideNumTextEl!.textContent = (cur + 1) + ' / ' + total;
      };
      updateBarSlideNum();
      on(EVENTS.SLIDE_CHANGED, updateBarSlideNum);
    }
  }

  if (drawerOnly && config.features.profile) {
    const hiddenProfile = document.createElement('div');
    hiddenProfile.className = 'lectura-bar-profile';
    hiddenProfile.setAttribute('aria-hidden', 'true');
    hiddenProfile.style.cssText =
      'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    document.body.appendChild(hiddenProfile);
    const profileApi = registerProfile(authService, { profileSlot: hiddenProfile }, {
      getSlidesVersion: () => config.slidesVersion,
      roleService: chatRoleService,
      presentationService: presentationManagementService,
      getPresentationId: () => wrappedAdapter.getPresId() || config.presentationId,
      excludeSettingsSections: ['tts', 'shortcuts']
    });
    if (typeof window !== 'undefined' && profileApi) {
      (window as unknown as { __lecturaOpenProfileSettings?: (section?: string) => void }).__lecturaOpenProfileSettings = (section?: string) => {
        const raw = String(section || '').trim().toLowerCase();
        profileApi.openSettings(isSettingsSectionId(raw) ? raw : 'tts');
      };
      const sectionFromQuery = getSettingsSectionFromLocation(window.location.href);
      if (sectionFromQuery) {
        window.setTimeout(() => profileApi.openSettings(sectionFromQuery), 0);
      }
    }
  }

  registerAuth(authService, drawerView, {
    signInGoogle: () => authService.signInGoogle?.(),
    signInGithub: () => authService.signInGithub?.()
  });

  // Load Firebase Auth (anonymous or linked user). Remote v1 POST /sessions runs when the user opens the
  // QR control — not on idle — so viewers do not hammer the API on every load.
  if (authService.ensureAuth) {
    authService.ensureAuth(() => {});
  }

  // Drawer-only hosts (e.g. remote shell) render their own FAB; reuse that badge for chat totals/unread.
  if (drawerOnly && !fabBadgeEl) {
    fabBadgeEl = wrappedAdapter.getBubbleBadgeElement();
  }

  if (config.features.chat) {
    registerChat(wrappedAdapter, drawerView, chatStorage, {
      fabBadge: fabBadgeEl ?? undefined,
      roleService: chatRoleService,
      ai: aiResourcesService,
      chatMentionNotify
    });
  } else if (typeof window !== 'undefined' && isChatToastDebugEnabled()) {
    console.info(
      '[lectura][chat-toast] chat.ts not registered: config.features.chat is false (inferFeatures needs a chat object, or set features.chat: true in __LECTURA_NEXT_CONFIG__)'
    );
  }
  if (config.features.notes) {
    registerNotes(wrappedAdapter, drawerView, notesStorage);
  }

  if (dom.speakerWrap) {
    async function runSpeakerCodeword(item: unknown): Promise<void> {
      if (drawerOnly) {
        const send = opts.sendRemoteTtsCodeword;
        if (!send) {
          remoteTtsVerbose('orchestrator: no sendRemoteTtsCodeword (drawer-only misconfigured?)');
        } else {
          const s = serializeQueueItemToCodeword(item as QueueItem);
          if (!s) {
            remoteTtsVerbose('codeword not serializable for remote (e.g. speak/pause)', {
              type: String((item as QueueItem)?.type ?? '')
            });
          } else {
            remoteTtsVerbose('orchestrator → sendRemoteTtsCodeword', { serial: s.slice(0, 96) });
            send(s);
          }
        }
        return;
      }
      if (ttsApi?.runCodeword) {
        await ttsApi.runCodeword(item);
        return;
      }
      const w = window as unknown as {
        RevealNotesTTS?: { runSingleCommand?: (cw: string, slide: Element | null, done: () => void) => void };
      };
      const it = item as QueueItem;
      const s = serializeQueueItemToCodeword(it);
      if (s && w.RevealNotesTTS?.runSingleCommand) {
        await new Promise<void>((resolve) => {
          const slide = typeof Reveal !== 'undefined' ? Reveal.getCurrentSlide?.() ?? null : null;
          w.RevealNotesTTS!.runSingleCommand!(s, slide, () => resolve());
        });
      }
    }
    registerSpeakerNotes(wrappedAdapter, drawerView, {
      roleService: chatRoleService,
      remoteSessionPresenter: opts.remotePresenterSession === true,
      verboseRemoteTtsClicks: typeof opts.sendRemoteTtsCodeword === 'function',
      runCodeword: runSpeakerCodeword
    });
  }

  if (config.features.rating) {
    const ratingApi = registerRating(
      {
        presentationId: config.presentationId,
        ratingLabel: config.ratingLabel,
        showRatingAfterSlide: config.showRatingAfterSlide as 'last' | number,
        formEndpoint: config.formEndpoint,
        ratingFormEndpoint: config.ratingFormEndpoint,
        confettiOnRate: config.confettiOnRate,
        chat: config.chat ?? undefined
      },
      wrappedAdapter,
      ratingStorage
    );
    ratingApi.renderRatingPill();
    on(EVENTS.SLIDE_CHANGED, () => ratingApi.refreshRatingPill());
  }

  if (config.features.resources) {
    if (!drawerOnly) {
      registerResources(wrappedAdapter, resourcesStorage, {
        slot: chatBarSlotEl ?? undefined,
        roleService: chatRoleService,
        ai: aiResourcesService
      });
    } else if (opts.resourcesBarSlot) {
      registerResources(wrappedAdapter, resourcesStorage, {
        slot: opts.resourcesBarSlot,
        roleService: chatRoleService,
        ai: aiResourcesService,
        sessionCanManageResources: opts.sessionCanManageResources
      });
    }
  }

  // Internal QA harness for the Resources->AI API before chat wiring.
  if (typeof window !== 'undefined' && aiResourcesService?.queryPresentation && aiResourcesService?.requestReindex) {
    window.__lecturaAIResources = {
      getPresentationId: () => wrappedAdapter.getPresId() || config.presentationId,
      query: (question: string, topK = 6) => {
        const presentationId = wrappedAdapter.getPresId() || config.presentationId;
        return aiResourcesService.queryPresentation!(presentationId, { question, topK });
      },
      reindex: async (resourceId?: string) => {
        const presentationId = wrappedAdapter.getPresId() || config.presentationId;
        return aiResourcesService.requestReindex!(presentationId, { resourceId });
      }
    };
  }

  const api: LecturaApi = {
    open: drawerApi.open,
    close: drawerApi.close,
    toggle: drawerApi.toggle,
    refreshSlideNumber: () => drawerView.setSlideNumber(adapter.getSlideIndex()),
    getCurrentUser: () => authService.getCurrentUser()
  };
  if (config.features.chat) {
    api.openChatWithDraft = (text: string) => {
      drawerApi.setTab('chat');
      drawerApi.open();
      const t = String(text ?? '').trim();
      window.queueMicrotask(() => emit(EVENTS.OPEN_CHAT_COMPOSER, t));
    };
  }
  if (ttsApi) {
    api.toggleTTS = ttsApi.toggleTTS;
    if (ttsApi.runCodeword) api.runCodeword = ttsApi.runCodeword;
  }
  return api;
}
